import React, { useState, useEffect, useCallback, useMemo } from 'react';
import config from '../config';
import authService from '../services/authService';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/AlertDialog";
import { Copy, Trash2, UserPlus, Mail, Edit, ShieldCheck, UserCog, UserMinus } from "lucide-react"; 

const AdminDashboard = ({ user, selectedTeam, currentUserId }) => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [outstandingInvites, setOutstandingInvites] = useState([]);
  const [showAcceptedInvites, setShowAcceptedInvites] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null); // State for copy feedback
  
  // New user invite state
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'User' });
  const [inviteLink, setInviteLink] = useState('');
  
  // Delete confirmation state
  const [inviteToDelete, setInviteToDelete] = useState(null);
  
  // User role state
  const [userRole, setUserRole] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Determine user's role for the selected team
  useEffect(() => {
    console.log('🔍 AdminDashboard useEffect - User Object:', user);
    console.log('🔍 AdminDashboard useEffect - Selected Team:', selectedTeam);
    console.log('🔍 AdminDashboard useEffect - CurrentUserId:', currentUserId);
    
    if (!user || !selectedTeam) {
      console.warn('🚨 Missing user or selectedTeam data');
      setUserRole(null);
      setIsAdmin(false);
      return;
    }

    // Handle nested user object structure
    const userData = user.user || user;
    console.log('🔍 Extracted user data:', userData);

    // Check if the user is an admin based on the role property
    if (userData.role) {
      console.log(`✅ Found role '${userData.role}' in user data`);
      const isAdminRole = userData.role === 'Admin';
      setUserRole(userData.role);
      setIsAdmin(isAdminRole);
      
      if (isAdminRole) {
        console.log('✅ User is an Admin!');
      } else {
        console.log('⚠️ User is not an Admin.');
      }
    } else {
      // No role found, fallback to default
      console.warn('⚠️ No role found in user data, setting to Admin by default');
      setUserRole('Admin');
      setIsAdmin(true);
    }
  }, [user, selectedTeam, currentUserId]);

  const fetchTeamMembers = useCallback(async () => {
    if (!selectedTeam?.teamId) {
      console.log('No team selected, cannot fetch team members');
      setTeamMembers([]);
      return;
    }
    
    try {
      const apiUrl = `${config.apiBaseUrl}/api/teams/${selectedTeam.teamId}/members`;
      console.log(`Fetching team members from: ${apiUrl}`);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.members)) {
          console.log('Team members fetched:', data.members);
          const normalizedMembers = data.members.map((member) => ({
            id: member.userId || member.id,
            email: member.email,
            role: member.role,
            username: member.username,
            joinedAt: member.joinedAt || member.createdAt,
          }));
          setTeamMembers(normalizedMembers);
        } else {
          console.error('Failed to parse team members from response:', data);
          setError('Failed to parse team members data');
          setTeamMembers([]);
        }
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch team members:', response.status, errorText);
        setError(`Failed to fetch team members (${response.status})`);
        setTeamMembers([]);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      setError('Error fetching team members');
      setTeamMembers([]);
    }
  }, [selectedTeam]);

  const fetchOutstandingInvites = useCallback(async () => {
    if (!selectedTeam) return;
    console.log('AdminDashboard: Fetching outstanding invites for team:', selectedTeam.teamName);
    setError('');
    setOutstandingInvites([]);

    try {
      const token = authService.getToken(); // Get the JWT token
      if (!token) {
        throw new Error('Authentication token not found.');
      }
      
      // --- CHANGE HERE: Use new endpoint --- 
      const response = await fetch(`${config.apiBaseUrl}/api/teams/${selectedTeam.teamId}/invites`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to fetch invites' }));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // --- CHANGE HERE: Get invites from data.invites --- 
      if (data.success && Array.isArray(data.invites)) {
        console.log('AdminDashboard: Outstanding invites fetched:', data.invites);
        const normalizedInvites = data.invites.map((invite) => ({
          inviteId: invite.inviteId,
          inviteToken: invite.inviteToken,
          email: invite.email,
          role: invite.role,
          status: invite.status,
          invitedAt: invite.invitedAt,
          invitedBy: invite.invitedBy,
        }));
        setOutstandingInvites(normalizedInvites);
      } else {
        setOutstandingInvites([]);
        throw new Error(data.error || 'Failed to fetch invites');
      }
    } catch (error) {
      console.error('AdminDashboard: Error fetching outstanding invites:', error);
      setError(error.message);
      setOutstandingInvites([]); // Clear invites on error
    }
  }, [selectedTeam]);

  // Fetch data when component mounts or dependencies change
  useEffect(() => {
    if (selectedTeam) {
      console.log('Selected team changed, fetching data...');
      fetchTeamMembers();
      fetchOutstandingInvites();
    }
  }, [fetchTeamMembers, fetchOutstandingInvites, selectedTeam]);

  const handleCopyInviteLink = async (inviteToken) => {
    if (!inviteToken) return;
    const fullInviteLink = `${window.location.origin}/invite/${inviteToken}`;
    try {
      await navigator.clipboard.writeText(fullInviteLink);
      setCopiedToken(inviteToken); // Set token for feedback
      console.log('Invite link copied:', fullInviteLink);
      // Reset feedback after a short delay
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (err) {
      console.error('Failed to copy invite link:', err);
      setError('Failed to copy link. Please copy it manually.');
      setCopiedToken(null); // Clear feedback on error
    }
  };

  const handleGenerateInvite = async () => {
    // Clear previous link/errors
    setInviteLink('');
    setError('');
    setSuccess('');
    
    if (!inviteForm.email || !inviteForm.role || !selectedTeam?.teamId) {
      setError('Please fill in all fields and ensure a team is selected');
      return;
    }
    
    // Check if user has admin permissions for this team
    if (!isAdmin) {
      setError('Only team administrators can invite new members');
      return;
    }
    
    try {
      // Use the correct endpoint: POST /api/teams/:teamId/invites
      const apiUrl = `${config.apiBaseUrl}/api/teams/${selectedTeam.teamId}/invites`;
      console.log('Sending invite request to:', apiUrl);
      
      // Send only email and role in the body
      const inviteData = { 
        email: inviteForm.email, 
        role: inviteForm.role 
      };
      
      console.log('Sending invite with data:', inviteData);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders(),
        },
        body: JSON.stringify(inviteData),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.inviteToken) {
          console.log('Invite created successfully:', data);
          // Construct the full invite link using the returned token
          const fullInviteLink = `${window.location.origin}/invite/${data.inviteToken}`;
          setInviteLink(fullInviteLink);
          setSuccess('Invitation created successfully');
          // Clear form
          setInviteForm({ email: '', role: 'User' }); 
          fetchOutstandingInvites(); // Refresh the invites list
        } else {
          throw new Error(data.error || 'Failed to get invite token from response');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorText = errorData.error || await response.text() || 'Unknown error';
        console.error('Failed to generate invite:', response.status, errorText);
        setError(`Failed to generate invite: ${errorText}`);
      }
    } catch (error) {
      console.error('Error generating invite:', error);
      setError(`Error generating invite: ${error.message || 'Unknown error'}`);
    }
  };

  const handleCopyInviteLinkFromInvite = () => {
    navigator.clipboard.writeText(inviteLink)
      .then(() => {
        console.log('Invite link copied to clipboard');
        setSuccess('Invite link copied to clipboard');
      })
      .catch((error) => {
        console.error('Failed to copy invite link:', error);
        setError('Failed to copy invite link');
      });
  };

  const handleDeleteInvite = async (inviteId) => {
    if (!isAdmin) {
      setError('Only team administrators can delete invites');
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/invites/${inviteId}`, {
        method: 'DELETE',
        headers: {
          ...authService.getAuthHeaders(),
        },
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        if (data.success) {
          setSuccess('Invite deleted successfully');
          setOutstandingInvites((prevInvites) =>
            prevInvites.filter((invite) => invite.inviteId !== inviteId)
          );
        } else {
          throw new Error(data.error || 'Failed to delete invite');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to delete invite (Status: ${response.status})`);
      }
    } catch (error) {
      console.error('Error deleting invite:', error);
      setError(error.message || 'Error deleting invite');
    } finally {
      setInviteToDelete(null);
    }
  };
  const handleRevokeInvite = async (inviteId) => {
    if (!isAdmin) {
      setError('Only admins can revoke invites.');
      return;
    }
    // Optional: Add a confirmation dialog
    if (!window.confirm('Are you sure you want to revoke this invitation?')) {
      return;
    }
    
    setError(''); // Clear previous errors
    setSuccess(''); // Clear previous success messages

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/invites/${inviteId}`, {
        method: 'DELETE',
        headers: {
          ...authService.getAuthHeaders(),
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSuccess('Invitation revoked successfully.');
          fetchOutstandingInvites(); // Refresh the list
        } else {
          throw new Error(data.error || 'Failed to revoke invite.');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to revoke invite (Status: ${response.status})`);
      }
    } catch (err) {
      console.error("Error revoking invite:", err);
      setError(err.message || 'An error occurred while revoking the invitation.');
    }
  };

  const toggleShowAcceptedInvites = () => {
    setShowAcceptedInvites(!showAcceptedInvites);
  };

  const invitesToDisplay = useMemo(() => {
    if (showAcceptedInvites) {
      return outstandingInvites;
    }
    return outstandingInvites.filter((invite) => (invite.status || (invite.Accepted ? 'accepted' : 'pending')) === 'pending');
  }, [outstandingInvites, showAcceptedInvites]);

  const formatStatus = (status) => {
    if (!status) {
      return 'Pending';
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const handleRemoveMember = async (memberUserId) => {
    if (!isAdmin || !selectedTeam) {
      setError('Only admins can remove members.');
      return;
    }
    if (memberUserId === currentUserId) {
      setError('You cannot remove yourself.');
      return;
    }
    if (!window.confirm('Are you sure you want to remove this member from the team?')) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/teams/${selectedTeam.teamId}/members/${memberUserId}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSuccess('Team member removed successfully.');
          fetchTeamMembers(); // Refresh the list
        } else {
          throw new Error(data.error || 'Failed to remove member.');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to remove member (Status: ${response.status})`);
      }
    } catch (err) {
      console.error("Error removing member:", err);
      setError(err.message || 'An error occurred while removing the member.');
    }
  };

  const handleChangeRole = async (memberUserId, newRole) => {
     if (!isAdmin || !selectedTeam) {
      setError('Only admins can change roles.');
      return;
    }
     if (!newRole) return; // Don't proceed if no role selected

     // Optional: Add confirmation, especially for demoting admins
     if (!window.confirm(`Are you sure you want to change this member's role to ${newRole}?`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/teams/${selectedTeam.teamId}/members/${memberUserId}`, {
        method: 'PUT',
        headers: {
            ...authService.getAuthHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: newRole }),
      });

       if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSuccess('Member role updated successfully.');
          fetchTeamMembers(); // Refresh the list
        } else {
          throw new Error(data.error || 'Failed to update role.');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to update role (Status: ${response.status})`);
      }
    } catch (err) {
      console.error("Error changing role:", err);
      setError(err.message || 'An error occurred while changing the role.');
    }
  };

  return (
    <div className="text-slate-100">
      <div className="max-w-5xl mx-auto">

        <header className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-semibold text-slate-100">Team Management</h1>
          {isAdmin && (
            <button
              onClick={() => setIsAddingUser(true)}
              className="flex items-center gap-2 bg-slate-800 border border-slate-700 hover:border-slate-600 px-4 py-2 rounded-lg text-sm transition-colors"
            >
              <UserPlus size={18} />
              Invite New Member
            </button>
          )}
        </header>

        {/* Alerts */}
        {error && (
          <div className="mb-6 p-3 bg-red-950/50 border border-red-800/50 text-red-300 rounded-lg text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 p-3 bg-emerald-950/50 border border-emerald-800/50 text-emerald-300 rounded-lg text-sm">
            {success}
          </div>
        )}

        {/* Team Members Section */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 text-slate-100">Team Members</h2>

          {teamMembers.length > 0 ? (
            <div className="space-y-3">
              {teamMembers.map((member) => (
                <div key={member.id || member.email} className="bg-slate-900/50 border border-slate-700 p-4 rounded-lg flex justify-between items-center">
                  {/* Member Info */}
                  <div>
                    <p className="font-medium text-slate-200">{member.email} {member.id === currentUserId && <span className="text-slate-500">(You)</span>}</p>
                    <p className="text-sm text-slate-400">Role: {member.role}</p>
                    {member.joinedAt && (
                      <p className="text-xs text-slate-500">Joined: {new Date(member.joinedAt).toLocaleDateString()}</p>
                    )}
                  </div>

                  {/* Admin Controls (only show if admin and not the current user) */}
                  {isAdmin && member.id !== currentUserId && (
                    <div className="flex items-center space-x-2">
                       {/* Role Change Dropdown */}
                      <select
                        value={member.role}
                        onChange={(e) => handleChangeRole(member.id, e.target.value)}
                        className="w-[120px] h-8 text-xs bg-slate-800 border border-slate-600 rounded text-slate-200 focus:outline-none focus:border-slate-500 [&>option]:bg-slate-800 [&>option]:text-slate-200"
                      >
                        <option value="User">User</option>
                        <option value="Admin">Admin</option>
                      </select>

                      {/* Remove Member Button */}
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        className="p-2 hover:bg-red-900/30 rounded text-red-400"
                        title="Remove Member"
                      >
                        <UserMinus size={16} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500">No team members found.</p>
          )}
        </div>

        {/* Invite New Member Section */}
        {console.log('About to render Invite section, isAdmin=', isAdmin)}
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 mb-6">
          {!isAddingUser ? (
            <button
              onClick={() => {
                setIsAddingUser(true);
                setInviteLink(''); // Clear previous link
                setError(''); // Clear errors
                setSuccess(''); // Clear success messages
              }}
              className="bg-indigo-600 hover:bg-indigo-500 text-white font-medium py-2 px-4 rounded flex items-center text-sm transition-colors"
            >
              <UserPlus className="mr-2 h-4 w-4" /> Invite New Member
            </button>
          ) : (
            <div>
              <h2 className="text-lg font-semibold mb-4 text-slate-100">Invite New Member</h2>
              <div className="space-y-3 mb-4">
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="w-full p-2 rounded bg-slate-800 border border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-slate-600"
                  required
                />
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  className="w-full p-2 rounded bg-slate-800 border border-slate-700 text-slate-200 focus:outline-none focus:border-slate-600 [&>option]:bg-slate-800"
                  required
                >
                  <option value="User">User</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleGenerateInvite}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white font-medium py-2 px-4 rounded flex items-center text-sm transition-colors"
                >
                  <Mail className="mr-2 h-4 w-4" /> Send Invite
                </button>
                <button
                  onClick={() => setIsAddingUser(false)}
                  className="bg-slate-700 hover:bg-slate-600 text-slate-200 font-medium py-2 px-4 rounded text-sm transition-colors"
                >
                  Cancel
                </button>
              </div>
              {inviteLink && (
                <div className="mt-4 bg-emerald-950/30 border border-emerald-800/30 p-4 rounded-lg">
                  <p className="text-sm mb-2 text-slate-300">Invite link generated (share this link):</p>
                  <div className="flex items-center space-x-2 bg-slate-900 border border-slate-700 p-2 rounded">
                    <input
                      type="text"
                      value={inviteLink}
                      readOnly
                      className="flex-grow bg-transparent text-sm text-slate-300"
                    />
                    <button onClick={handleCopyInviteLinkFromInvite} title="Copy Link" className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-slate-200">
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Outstanding Invites Section */}
        {isAdmin && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-slate-100">Pending Invites</h2>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-400">
                <input
                  type="checkbox"
                  checked={showAcceptedInvites}
                  onChange={toggleShowAcceptedInvites}
                  className="rounded bg-slate-700 border-slate-600 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-slate-900"
                />
                <span>Show Accepted Invites</span>
              </label>
            </div>

            {invitesToDisplay.length > 0 ? (
              <div className="space-y-3">
                {invitesToDisplay.map((invite) => {
                  const inviteToken = invite.inviteToken || invite.InviteToken;
                  const inviteId = invite.inviteId || invite.id;
                  const rawStatus = invite.status || (invite.Accepted ? 'accepted' : 'pending');
                  const statusLabel = formatStatus(rawStatus);
                  const isPending = (rawStatus || 'pending') === 'pending';

                  return (
                    <div key={inviteId || inviteToken} className="bg-slate-900/50 border border-slate-700 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium text-slate-200">{invite.email || invite.InviteeEmail}</p>
                          <p className="text-sm text-slate-400">Invited by: {invite.invitedBy || invite.InviterEmail || 'Unknown'}</p>
                          {invite.invitedAt && (
                            <p className="text-sm text-slate-500">Created: {new Date(invite.invitedAt).toLocaleDateString()}</p>
                          )}
                          <p className="text-sm text-slate-400">Status: {statusLabel}</p>
                        </div>

                        <div className="flex items-center gap-2">
                          {isPending && inviteToken && (
                            <>
                              <button
                                onClick={() => handleCopyInviteLink(inviteToken)}
                                className={`p-2 rounded ${copiedToken === inviteToken ? 'text-emerald-400' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'}`}
                                title="Copy invite link"
                                disabled={copiedToken === inviteToken}
                              >
                                {copiedToken === inviteToken ? <ShieldCheck size={18} /> : <Copy size={18} />}
                              </button>
                              <button
                                onClick={() => setInviteToDelete(invite)}
                                className="p-2 hover:bg-red-900/30 rounded text-red-400"
                                title="Delete invite"
                              >
                                <Trash2 size={18} />
                              </button>
                            </>
                          )}
                          {inviteId && (
                            <button
                              onClick={() => handleRevokeInvite(inviteId)}
                              className="bg-red-600 hover:bg-red-500 text-white text-xs font-medium py-1 px-2 rounded flex items-center transition-colors"
                              title="Revoke Invitation"
                              disabled={!isPending}
                            >
                              <Trash2 className="h-3 w-3 mr-1" /> Revoke
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-500">No pending invites.</p>
            )}
          </div>
        )}

        {/* New User Invite Modal */}
        {isAddingUser && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-slate-800 border border-slate-700 p-6 rounded-lg w-full max-w-md">
              <h2 className="text-lg font-semibold mb-4 text-slate-100">Invite New Team Member</h2>
              <div className="space-y-4">
                <input
                  type="email"
                  placeholder="Email Address"
                  className="w-full p-2 bg-slate-900 rounded border border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-slate-600"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                />
                <select
                  className="w-full p-2 bg-slate-900 rounded border border-slate-700 text-slate-200 focus:outline-none focus:border-slate-600 [&>option]:bg-slate-800 [&>option]:text-slate-200"
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                >
                  <option value="User">User</option>
                  <option value="Admin">Admin</option>
                </select>

                {inviteLink && (
                  <div className="mt-4 p-3 bg-slate-900/50 border border-slate-700 rounded">
                    <p className="mb-2 font-medium text-slate-300 text-sm">Invite Link:</p>
                    <div className="flex">
                      <input
                        type="text"
                        value={inviteLink}
                        readOnly
                        className="flex-1 p-2 bg-slate-950 rounded-l border border-slate-700 text-slate-300 text-sm"
                      />
                      <button
                        onClick={handleCopyInviteLinkFromInvite}
                        className="p-2 bg-indigo-600 hover:bg-indigo-500 rounded-r transition-colors"
                        title="Copy to clipboard"
                      >
                        <Copy size={18} />
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    onClick={() => {
                      setIsAddingUser(false);
                      setInviteForm({ email: '', role: 'User' });
                      setInviteLink('');
                    }}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded text-slate-200 text-sm transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGenerateInvite}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded flex items-center gap-2 text-sm transition-colors disabled:opacity-50"
                    disabled={!inviteForm.email || !inviteForm.role}
                  >
                    <Mail size={16} />
                    Generate Invite
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Invite Confirmation Dialog */}
        <AlertDialog open={!!inviteToDelete} onOpenChange={() => setInviteToDelete(null)}>
          <AlertDialogContent className="bg-gray-800 border border-gray-700">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete Invite</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-300">
                Are you sure you want to delete this invitation? This action cannot be undone.
                {inviteToDelete && (
                  <div className="mt-2 p-2 bg-white/5 rounded">
                    <p className="font-medium">{inviteToDelete.email || inviteToDelete.InviteeEmail}</p>
                    <p className="text-sm">Status: {formatStatus(inviteToDelete.status || (inviteToDelete.Accepted ? 'accepted' : 'pending'))}</p>
                  </div>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-gray-700 text-white hover:bg-gray-600">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                className="bg-red-600 text-white hover:bg-red-500"
                onClick={() => inviteToDelete && handleDeleteInvite(inviteToDelete.inviteId || inviteToDelete.id)}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default AdminDashboard;


